import { cn } from "@/lib/utils";
import { formatMs } from "@/utils/format";
import type { NormalizedTrace, TraceNode } from "@/utils/trace/model";

const KIND_DOT: Record<TraceNode["kind"], string> = {
  turn: "bg-trace-inference",
  tool_call: "bg-trace-tool",
  cw_span: "bg-trace-citation",
  tool_batch: "bg-trace-tool",
  async_delegation: "bg-trace-subagent",
  async_job: "bg-trace-subagent",
  sa_execution: "bg-trace-subagent",
  sa_model_turn: "bg-trace-subagent",
  sa_tool_call: "bg-trace-tool",
};

/**
 * Every node in execution order, as text.
 *
 * Collapsing each flow onto one row costs the per-tool row labels, and at a
 * minute-long span most tool bars are far too narrow to hold a name. This is
 * where the names are guaranteed to be readable — and it is also the visible
 * label the citation-worker green needs, since that hue sits under 3:1 against
 * the track surface.
 */
export function TraceNodeList({
  trace,
  selectedNodeId,
  onSelect,
}: {
  trace: NormalizedTrace;
  selectedNodeId: string | null;
  onSelect: (node: TraceNode) => void;
}) {
  // Grouped by lane and numbered within it. A single global counter would put
  // "#22 analysis" after the main flow's "#21", implying one sequence where
  // there are really several concurrent ones.
  const groups = trace.lanes
    .map((lane) => {
      const flat: TraceNode[] = [];
      const walk = (nodes: TraceNode[]) => {
        for (const node of nodes) {
          flat.push(node);
          walk(node.children);
        }
      };
      walk(lane.nodes);
      return {
        lane,
        nodes: flat
          .filter((node) => node.startMs !== null)
          .sort((a, b) => (a.startMs ?? 0) - (b.startMs ?? 0)),
      };
    })
    .filter((group) => group.nodes.length > 0);

  if (groups.length === 0) return null;

  return (
    <div className="grid gap-1.5">
      {groups.map(({ lane, nodes }) => (
        <div key={lane.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[13px]">
          {groups.length > 1 && (
            <span className="text-xs font-medium text-muted-foreground">{lane.label}</span>
          )}
          <ol className="flex flex-wrap gap-x-3 gap-y-1">
            {nodes.map((node, index) => (
              <li key={node.id}>
                <button
                  type="button"
                  onClick={() => onSelect(node)}
                  aria-pressed={node.id === selectedNodeId}
                  className={cn(
                    "flex cursor-pointer items-center gap-1.5 rounded-sm px-1 py-0.5 hover:bg-surface-subtle",
                    node.id === selectedNodeId && "bg-surface-subtle font-medium",
                  )}
                >
                  <span className="text-muted-foreground">#{index + 1}</span>
                  {/* Small: with one of these per entry, a full-size swatch
                      turns a 20-item list into a wall of colour. */}
                  <span
                    aria-hidden
                    className={cn("size-1.5 shrink-0 rounded-full", KIND_DOT[node.kind])}
                  />
                  <span className={cn(node.status === "error" && "text-destructive")}>
                    {node.approximate ? `≈ ${node.label}` : node.label}
                  </span>
                  <span className="text-muted-foreground">
                    {node.openEnded ? "미완료" : formatMs(node.durationMs)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}
